import React from 'react';
import clsx from 'clsx'; // Import clsx
import type { IProps, ButtonProps, AnchorProps } from '@/elements/Button/Button.interface';
import Icon from '@/elements/Icon/Icon.component'; // Import Icon component
import type { IconName } from '@/elements/Icon/Icon.interface'; // Import IconName type
import '@/elements/Button/Button.styles.css'; // Import styles

// Type guard to check if props are for an anchor element
function isAnchorProps(props: IProps): props is AnchorProps {
    return props.as === 'a';
}

// Type guard to check if props are for a button element
function isButtonProps(props: IProps): props is ButtonProps {
    return props.as === 'button' || typeof props.as === 'undefined';
}

const Button = React.forwardRef<HTMLButtonElement | HTMLAnchorElement, IProps>((props, ref) => {
    // Destructure common props first
    const {
        children,
        variant = 'primary',
        color = 'primary',
        size = 'base', // Add size prop with default
        icon,
        iconPosition = 'before',
        className = '',
    } = props;

    // Base class using BEM-like convention
    const baseClass = 'button';

    // Construct variant, color, and size classes using the requested convention
    const variantClass = `button__type--${variant}`;
    const colorClass = `button__color--${color}`;
    const sizeClass = `button__size--${size}`;

    // Combine all classes using clsx
    const combinedClassName = clsx(baseClass, variantClass, colorClass, sizeClass, className);

    // Icon element using the Icon component
    const iconElement = icon ? (
        <span className={clsx('button__icon', `button__icon--${iconPosition}`)} aria-hidden="true">
            <Icon icon={icon as keyof IconName} /> {/* Use Icon component */}
        </span>
    ) : null;

    // Render based on type guard, destructuring specific props inside
    if (isAnchorProps(props)) {
        // Destructure anchor-specific props directly from `props`
        // Remove base props and 'as' from being spread
        const { children: _c, variant: _v, color: _co, size: _s, icon: _i, iconPosition: _ip, className: _cl, as, href, ...anchorSpecificProps } = props;
        return (
            <a
                ref={ref as React.Ref<HTMLAnchorElement>} // Forward ref
                href={href} // Use the required href
                className={combinedClassName} // Use clsx combined class name
                {...anchorSpecificProps} // Spread remaining valid anchor attributes
            >
                {icon && iconPosition === 'before' ? iconElement : undefined}
                {children && <span className="button__text"  dangerouslySetInnerHTML={{ __html: children as string}}/>}
                {icon && iconPosition === 'after' ? iconElement : undefined}
            </a>
        );
    }

    // Default to button if not anchor (covers as='button' and as=undefined)
    // Destructure button-specific props directly from `props`
    // Remove base props and 'as' from being spread
    const { children: _c, variant: _v, color: _co, size: _s, icon: _i, iconPosition: _ip, className: _cl, as, type = 'button', ...buttonSpecificProps } = props as ButtonProps; // Cast needed here for default type
    return (
        <button
            ref={ref as React.Ref<HTMLButtonElement>} // Forward ref
            type={type} // Use the correctly typed 'type'
            className={combinedClassName} // Use clsx combined class name
            {...buttonSpecificProps} // Spread remaining valid button attributes
        >
            {iconPosition === 'before' && iconElement}
            {children &&
            (typeof children === 'string' ? (
                <span
                className="button__text"
                dangerouslySetInnerHTML={{ __html: children }}
                />
            ) : (
                <span className="button__text">{children}</span>
            ))}
            {iconPosition === 'after' && iconElement}
        </button>
    );
});

Button.displayName = 'Button'; // Add display name for better debugging

export default Button;
